<template>
    <div>
        <SetupPage
            page-label="Academic Sessions"
            eyebrow="SCHOOL CALENDAR"
            title="Academic sessions"
            description="Create the school years available for registration, terms and student records."
            toolbar-title="Registered sessions"
            :meta="scopeLabel"
            :loader="onloader"
        >
            <template slot="side">
                <div v-if="sessionitems !== null" class="ws-stat">
                    <strong>{{ sessions.length }}</strong>
                    <span>{{ sessions.length === 1 ? "session" : "sessions" }}</span>
                </div>
                <button type="button" class="btn ws-primary-button" @click="openCreate">
                    <i class="fa fa-plus" aria-hidden="true"></i>
                    <span>New session</span>
                </button>
            </template>

            <template slot="toolbar">
                <div class="ws-search">
                    <i class="fa fa-search" aria-hidden="true"></i>
                    <input
                        v-model="searchval"
                        type="search"
                        placeholder="Search sessions"
                        aria-label="Search academic sessions"
                        :disabled="!sessions.length"
                    />
                    <button
                        v-if="searchval"
                        type="button"
                        class="ws-search__clear"
                        aria-label="Clear search"
                        @click="searchval = ''"
                    >
                        <i class="fa fa-times" aria-hidden="true"></i>
                    </button>
                </div>
            </template>

            <div v-if="sessionitems === null && !loadFailed" class="ws-state">
                <i class="fa fa-spinner fa-spin" aria-hidden="true"></i>
                <strong>Loading sessions</strong>
                <span>Fetching the academic sessions for your school.</span>
            </div>

            <div v-else-if="loadFailed && !sessions.length" class="ws-state">
                <i class="fa fa-exclamation-circle" aria-hidden="true"></i>
                <strong>Sessions could not be loaded</strong>
                <span>Check your connection and try again.</span>
                <button type="button" class="btn ws-ghost-button" @click="retryLoad">
                    <i class="fa fa-refresh" aria-hidden="true"></i>
                    <span>Try again</span>
                </button>
            </div>

            <div v-else-if="!sessions.length" class="ws-state">
                <i class="fa fa-calendar" aria-hidden="true"></i>
                <strong>No sessions yet</strong>
                <span>Create the first academic session to start the school calendar.</span>
                <button type="button" class="btn ws-ghost-button" @click="openCreate">
                    <i class="fa fa-plus" aria-hidden="true"></i>
                    <span>Add session</span>
                </button>
            </div>

            <div v-else-if="!filteredSessions.length" class="ws-state">
                <i class="fa fa-search" aria-hidden="true"></i>
                <strong>No sessions match “{{ searchval.trim() }}”</strong>
                <span>Try a different session name.</span>
                <button type="button" class="btn ws-ghost-button" @click="searchval = ''">
                    Clear search
                </button>
            </div>

            <div v-else class="ws-grid" :class="{ 'is-loading': isLoading }">
                <article
                    v-for="(item, index) in filteredSessions"
                    :key="rowKey(item, index)"
                    class="ws-card"
                >
                    <span class="ws-card__index">{{ String(index + 1).padStart(2, "0") }}</span>
                    <div class="ws-card__body">
                        <span class="ws-card__title">{{ item.SchSession || "—" }}</span>
                        <span class="ws-card__meta">Academic session</span>
                    </div>
                    <div class="ws-card__actions">
                        <button
                            type="button"
                            class="ws-edit-button"
                            :aria-label="`Edit ${item.SchSession || 'session'}`"
                            @click="editSession(item.Id)"
                        >
                            <i class="fa fa-pencil" aria-hidden="true"></i>
                            <span>Edit</span>
                        </button>
                    </div>
                </article>
            </div>
        </SetupPage>

        <SetupDrawer
            :show.sync="formVisible"
            icon="fa-calendar"
            :eyebrow="editingId ? 'UPDATE RECORD' : 'NEW RECORD'"
            :title="editingId ? 'Edit session' : 'Add a session'"
            :subtitle="editingId ? form.name : 'Name the school year, for example 2026/2027.'"
            :saving="isSaving"
            :submit-label="editingId ? 'Save changes' : 'Create session'"
            form-id="session-form"
            @close="closeForm"
            @submit="submitSession"
        >
            <fieldset class="ws-fieldset">
                <legend>Session details</legend>
                <div class="form-group">
                    <label for="session-name">
                        Session name <span class="ws-required">*</span>
                    </label>
                    <input
                        id="session-name"
                        v-model="form.name"
                        type="text"
                        class="form-control"
                        :class="{ 'is-invalid': errors.name }"
                        placeholder="e.g. 2026/2027"
                        maxlength="40"
                        autocomplete="off"
                        :disabled="isSaving"
                        @input="clearError('name')"
                    />
                    <span v-if="errors.name" class="ws-error" role="alert">
                        {{ errors.name }}
                    </span>
                    <span v-else class="ws-hint">
                        Use the school’s standard session format. Sessions are used across
                        terms, classes and reports.
                    </span>
                </div>
            </fieldset>
        </SetupDrawer>
    </div>
</template>

<script>
import Alert from "@/mixins/Alert.mixins";
import SetupPage from "@/components/SetupPage";
import SetupDrawer from "@/components/SetupDrawer";

const EMPTY_FORM = () => ({ name: "" });

export default {
    name: "Session",
    mixins: [Alert],
    components: {
        SetupPage,
        SetupDrawer
    },
    data() {
        return {
            accessDetails: null,
            sessionitems: null,
            searchval: "",
            isLoading: false,
            loadFailed: false,
            requestSeq: 0,
            formVisible: false,
            isSaving: false,
            editingId: 0,
            form: EMPTY_FORM(),
            errors: {},
            onloader: "none"
        };
    },
    computed: {
        sessions() {
            return Array.isArray(this.sessionitems) ? this.sessionitems : [];
        },
        hasSearch() {
            return this.searchval.trim().length > 0;
        },
        filteredSessions() {
            const tokens = this.searchval
                .toLowerCase()
                .split(/\s+/)
                .filter(Boolean);

            if (!tokens.length) return this.sessions;

            return this.sessions.filter(item => {
                const haystack = [item.SchSession].filter(Boolean).join(" ").toLowerCase();
                return tokens.every(token => haystack.includes(token));
            });
        },
        scopeLabel() {
            if (this.sessionitems === null) return "Loading sessions…";
            if (this.loadFailed) return "Sessions unavailable.";
            if (this.hasSearch) {
                return `${this.filteredSessions.length} of ${this.sessions.length} shown`;
            }
            const count = this.sessions.length;
            return `${count} ${count === 1 ? "session" : "sessions"}`;
        }
    },
    methods: {
        rowKey(item, index) {
            if (item && item.Id !== undefined && item.Id !== null && item.Id !== "") {
                return item.Id;
            }
            return `session-${index}`;
        },
        errorMessage(error, fallback) {
            const data = error && error.response && error.response.data;
            if (typeof data === "string" && data.trim()) return data.trim();
            if (data && typeof data === "object") {
                return data.message || data.responseMessage || fallback;
            }
            if (error && error.message) return error.message;
            return fallback;
        },
        async loadSessions() {
            const sequence = ++this.requestSeq;
            this.isLoading = true;
            if (this.sessionitems === null) this.loadFailed = false;

            try {
                const resp = await this.$store.dispatch("getsession");
                if (sequence !== this.requestSeq) return;
                this.sessionitems = Array.isArray(resp) ? resp : [];
                this.loadFailed = false;
            } catch (error) {
                if (sequence !== this.requestSeq) return;
                this.loadFailed = true;
                this.sessionitems = [];
                this.ShowAlertError(
                    this.errorMessage(error, "Unable to load academic sessions."),
                    ""
                );
            } finally {
                if (sequence === this.requestSeq) this.isLoading = false;
            }
        },
        retryLoad() {
            this.sessionitems = null;
            this.loadFailed = false;
            this.loadSessions();
        },
        resetForm() {
            this.editingId = 0;
            this.errors = {};
            this.form = EMPTY_FORM();
        },
        openCreate() {
            this.resetForm();
            this.formVisible = true;
        },
        async editSession(Id) {
            this.onloader = "flex";
            try {
                const resp = await this.$store.dispatch("getsessionbyid", Id);
                if (!resp) throw new Error("The selected session could not be loaded.");

                this.resetForm();
                this.editingId = Id || 0;
                this.form = { name: resp.SchSession || "" };
                this.formVisible = true;
            } catch (error) {
                this.ShowAlertError(
                    this.errorMessage(error, "Unable to load this session."),
                    ""
                );
            } finally {
                this.onloader = "none";
            }
        },
        closeForm() {
            this.formVisible = false;
            this.resetForm();
        },
        clearError(field) {
            if (!this.errors[field]) return;
            const errors = { ...this.errors };
            delete errors[field];
            this.errors = errors;
        },
        validate() {
            const errors = {};
            if (!String(this.form.name || "").trim()) {
                errors.name = "Enter the session name.";
            }
            this.errors = errors;

            if (Object.keys(errors).length) {
                const element = document.getElementById("session-name");
                if (element) element.focus();
            }

            return Object.keys(errors).length === 0;
        },
        async submitSession() {
            if (this.isSaving) return;
            if (!this.validate()) return;

            const isEdit = Boolean(this.editingId);
            const payload = {
                Id: this.editingId || 1,
                schsession: String(this.form.name || "").trim(),
                processop: isEdit ? "U" : "N",
                username: this.$store.state.auth.username,
                accesskey: this.$store.state.auth.accesskey
            };

            this.isSaving = true;
            this.onloader = "flex";
            try {
                const resp = await this.$store.dispatch("sessionregistration", payload);

                if (resp && resp.responseCode && resp.responseCode !== "00") {
                    this.ShowAlertError(
                        resp.responseMessage || "The session could not be saved.",
                        ""
                    );
                    return;
                }

                this.closeForm();
                this.searchval = "";
                await this.loadSessions();

                if (this.loadFailed) {
                    this.ShowAlertWarning(
                        "Session saved, but the updated list could not be loaded.",
                        ""
                    );
                } else {
                    this.ShowAlertSuccess(
                        isEdit ? "Session updated successfully." : "Session created successfully.",
                        ""
                    );
                }
            } catch (error) {
                this.ShowAlertError(
                    this.errorMessage(error, "Unable to save this session at the moment."),
                    ""
                );
            } finally {
                this.isSaving = false;
                this.onloader = "none";
            }
        }
    },
    mounted() {
        const userDetails = this.$store.getters["auth/_user"];
        if (!userDetails || userDetails.isSchoolStaff == false) {
            this.$store.dispatch("auth/Logout");
            this.$router.push("/login");
            return;
        }
        this.loadSessions();
    }
};
</script>
